import { useState, useCallback } from 'react';
import ToolLayout from '../../components/layout/ToolLayout';
import { Button, Card, CardContent, CopyButton, Select, Badge, toast } from '@devkit/ui';
import { Hash, Copy, RefreshCw, Download } from 'lucide-react';
import { trackToolExecute, trackCopy, trackDownload } from '@devkit/analytics';

const versionOptions = [
  { value: 'v4', label: 'UUID v4 (Random)' },
  { value: 'v7', label: 'UUID v7 (Time-ordered)' },
];

// Generate UUID v4
function generateUUIDv4(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Generate UUID v7
function generateUUIDv7(): string {
  const timestamp = Date.now();
  const timeHex = timestamp.toString(16).padStart(12, '0');
  const random = generateUUIDv4().replace(/-/g, '').substring(8);
  return `${timeHex.substring(0, 8)}-${timeHex.substring(8, 12)}-7${random.substring(1, 4)}-${random.substring(4, 8)}-${random.substring(8, 20)}`;
}

export default function UuidGeneratorPage() {
  const [version, setVersion] = useState('v4');
  const [count, setCount] = useState(1);
  const [uuids, setUuids] = useState<string[]>([]);

  const generate = useCallback(() => {
    const newUuids: string[] = [];
    for (let i = 0; i < count; i++) {
      newUuids.push(version === 'v4' ? generateUUIDv4() : generateUUIDv7());
    }
    setUuids(newUuids);
    trackToolExecute('uuid-generator', `generate-${version}`);
  }, [version, count]);

  const copyAll = async () => {
    if (uuids.length === 0) return;
    const text = uuids.join('\n');
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for non-secure contexts (e.g. http://192.168.x.x)
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.top = '0';
        textarea.style.left = '0';
        textarea.style.opacity = '0';
        textarea.style.pointerEvents = 'none';
        document.body.appendChild(textarea);
        textarea.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (!ok) throw new Error('execCommand copy failed');
      }
      trackCopy('uuid-generator');
      toast.success(`Copied ${uuids.length} UUID${uuids.length > 1 ? 's' : ''} to clipboard`);
    } catch (err) {
      toast.error('Failed to copy. Try using the Download button instead.');
      console.error('Copy failed:', err);
    }
  };

  const downloadAll = () => {
    const blob = new Blob([uuids.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `uuids-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    trackDownload('uuid-generator');
  };

  return (
    <ToolLayout slug="uuid-generator">
      <Card>
        <CardContent className="p-6">
          {/* Options */}
          <div className="flex flex-wrap gap-4 mb-6 pb-6 border-b border-slate-700">
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-400">Version:</label>
              <Select
                options={versionOptions}
                value={version}
                onChange={setVersion}
                className="w-56"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-400">Count:</label>
              <input
                type="number"
                min="1"
                max="100"
                value={count}
                onChange={(e) => setCount(Math.min(100, Math.max(1, parseInt(e.target.value) || 1)))}
                className="w-20 px-3 py-2 bg-surface border border-slate-700 rounded-lg font-mono text-sm text-slate-100 focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          {/* Version Info */}
          <div className="mb-6 p-4 bg-surface/50 rounded-lg border border-slate-700/50">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <Hash className="w-4 h-4 text-primary" />
              </div>
              <div>
                <h3 className="font-medium text-white mb-1">
                  {version === 'v4' ? 'UUID v4 (Random)' : 'UUID v7 (Time-ordered)'}
                </h3>
                <p className="text-sm text-slate-400">
                  {version === 'v4'
                    ? 'Random UUID based on cryptographically strong random numbers. Suitable for most use cases.'
                    : 'Time-ordered UUID that sorts chronologically. Better for database indexing and time-based sorting.'}
                </p>
              </div>
            </div>
          </div>

          {/* Generate Button */}
          <Button onClick={generate} className="gap-2 w-full sm:w-auto mb-6">
            <RefreshCw className="w-4 h-4" />
            Generate {count} UUID{count > 1 ? 's' : ''}
          </Button>

          {/* Generated UUIDs */}
          {uuids.length > 0 && (
            <>
              <div className="mb-4 p-4 bg-surface rounded-lg border border-slate-700">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-slate-300">
                    Generated UUID{count > 1 ? 's' : ''}
                  </label>
                  <div className="flex items-center gap-2">
                    <Badge variant="default">{uuids.length}</Badge>
                  </div>
                </div>
                <div className="space-y-2">
                  {uuids.map((uuid, i) => (
                    <div key={i} className="flex items-center gap-2 group">
                      <code className="flex-1 text-sm text-primary font-mono bg-slate-800/50 px-3 py-2 rounded">
                        {uuid}
                      </code>
                      <CopyButton text={uuid} onCopy={() => trackCopy('uuid-generator')} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Bulk Actions */}
              <div className="flex gap-2">
                <Button variant="outline" onClick={copyAll} className="gap-2">
                  <Copy className="w-4 h-4" />
                  Copy All
                </Button>
                <Button variant="outline" onClick={downloadAll} className="gap-2">
                  <Download className="w-4 h-4" />
                  Download
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      </ToolLayout>
  );
}
